import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { staffApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminStaff } from '../../api/types.js';
import { Banner } from '../../components/Banner.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { SortableColumnHeader } from '../../components/table/SortableColumnHeader.js';
import { FilterSummary, RangeFilterInput, SelectFilterInput, TextFilterInput } from '../../components/table/FilterInputs.js';
import { useTableSort } from '../../lib/dataTable/useTableSort.js';
import { useColumnFilters } from '../../lib/dataTable/useColumnFilters.js';
import { numberRangeColumn, selectColumn, textContainsColumn } from '../../lib/dataTable/filters.js';
import { useImpersonationStore } from '../../state/impersonationStore.js';
import { StaffFormModal } from './StaffFormModal.js';
import { SetPinModal } from './SetPinModal.js';
import { SetPasswordModal } from './SetPasswordModal.js';
import { formatDateTime } from '../../lib/format.js';

export function StaffPage() {
  const { t } = useI18n();
  const [staff, setStaff] = useState<AdminStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminStaff | 'new' | null>(null);
  const [pinTarget, setPinTarget] = useState<AdminStaff | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<AdminStaff | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminStaff | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const startImpersonation = useImpersonationStore((s) => s.start);

  function flashStatus(message: string) {
    setStatus(message);
    window.setTimeout(() => setStatus((current) => (current === message ? null : current)), 4000);
  }

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setStaff(await staffApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const columnFilters = useColumnFilters(staff, {
    code: textContainsColumn<AdminStaff>((s) => s.code),
    displayName: textContainsColumn<AdminStaff>((s) => s.displayName),
    role: selectColumn<AdminStaff>((s) => s.role),
    discountCeilingPct: numberRangeColumn<AdminStaff>((s) => s.discountCeilingPct),
    active: selectColumn<AdminStaff>((s) => String(s.active)),
  });

  const sort = useTableSort(columnFilters.rows, {
    code: { type: 'string', value: (s: AdminStaff) => s.code },
    displayName: { type: 'string', value: (s: AdminStaff) => s.displayName },
    role: { type: 'string', value: (s: AdminStaff) => s.role },
    discountCeilingPct: { type: 'number', value: (s: AdminStaff) => s.discountCeilingPct },
    active: { type: 'boolean', value: (s: AdminStaff) => s.active },
  });
  const visibleStaff = sort.rows;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('staff.title')}</h1>
          <p>{t('staff.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
          {t('staff.new')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}
      {status && <Banner kind="ok">{status}</Banner>}

      <FilterSummary activeCount={columnFilters.activeCount} onClear={columnFilters.clear} />

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : staff.length > 0 && visibleStaff.length === 0 ? (
        <p>{t('table.noMatches')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableColumnHeader label={t('staff.code')} ariaSort={sort.ariaSortFor('code')} onSort={() => sort.toggleSort('code')} />
                <SortableColumnHeader
                  label={t('staff.displayName')}
                  ariaSort={sort.ariaSortFor('displayName')}
                  onSort={() => sort.toggleSort('displayName')}
                />
                <SortableColumnHeader label={t('staff.role')} ariaSort={sort.ariaSortFor('role')} onSort={() => sort.toggleSort('role')} />
                <SortableColumnHeader
                  label={t('staff.discountCeiling')}
                  align="right"
                  ariaSort={sort.ariaSortFor('discountCeilingPct')}
                  onSort={() => sort.toggleSort('discountCeilingPct')}
                />
                <SortableColumnHeader label={t('common.status')} ariaSort={sort.ariaSortFor('active')} onSort={() => sort.toggleSort('active')} />
                <th>{t('common.actions')}</th>
              </tr>
              <tr className="table-filter-row">
                <th>
                  <TextFilterInput
                    id="staff-filter-code"
                    column={t('staff.code')}
                    value={columnFilters.values.code as string}
                    onChange={(value) => columnFilters.setValue('code', value)}
                  />
                </th>
                <th>
                  <TextFilterInput
                    id="staff-filter-name"
                    column={t('staff.displayName')}
                    value={columnFilters.values.displayName as string}
                    onChange={(value) => columnFilters.setValue('displayName', value)}
                  />
                </th>
                <th>
                  <SelectFilterInput
                    id="staff-filter-role"
                    column={t('staff.role')}
                    value={columnFilters.values.role as string}
                    onChange={(value) => columnFilters.setValue('role', value)}
                    options={[
                      { value: 'admin', label: t('staff.roleAdmin') },
                      { value: 'staff', label: t('staff.roleStaff') },
                    ]}
                  />
                </th>
                <th className="numeric">
                  <RangeFilterInput
                    id="staff-filter-ceiling"
                    column={t('staff.discountCeiling')}
                    value={columnFilters.values.discountCeilingPct as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('discountCeilingPct', value)}
                    affix={{ symbol: '%', position: 'suffix' }}
                    placeholder="0"
                  />
                </th>
                <th>
                  <SelectFilterInput
                    id="staff-filter-active"
                    column={t('common.status')}
                    value={columnFilters.values.active as string}
                    onChange={(value) => columnFilters.setValue('active', value)}
                    options={[
                      { value: 'true', label: t('common.active') },
                      { value: 'false', label: t('common.inactive') },
                    ]}
                  />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleStaff.map((s) => (
                <tr key={s.id}>
                  <td className="numeric">{s.code}</td>
                  <td>
                    {s.displayName}
                    {s.lockedUntil && (
                      <div className="field-hint">{t('staff.lockedUntil', { time: formatDateTime(s.lockedUntil) })}</div>
                    )}
                  </td>
                  <td>{s.role === 'admin' ? t('staff.roleAdmin') : t('staff.roleStaff')}</td>
                  <td className="numeric">{s.discountCeilingPct}%</td>
                  <td>
                    <span className={`badge badge--${s.active ? 'ok' : 'neutral'}`}>
                      {s.active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(s)}>
                      {t('common.edit')}
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPinTarget(s)}>
                      {t('staff.setPin')}
                    </button>
                    {s.role === 'admin' && (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPasswordTarget(s)}>
                        {t('staff.setPassword')}
                      </button>
                    )}
                    {s.role === 'staff' && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => startImpersonation({ id: s.id, displayName: s.displayName, code: s.code })}
                      >
                        {t('impersonation.start')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setDeactivateTarget(s)}
                    >
                      {s.active ? t('staff.deactivate') : t('staff.activate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <StaffFormModal
          staff={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setStaff((prev) => {
              const exists = prev.some((s) => s.id === saved.id);
              return exists ? prev.map((s) => (s.id === saved.id ? saved : s)) : [...prev, saved];
            });
            setEditing(null);
          }}
        />
      )}

      {pinTarget && <SetPinModal staff={pinTarget} onClose={() => setPinTarget(null)} />}

      {passwordTarget && <SetPasswordModal staff={passwordTarget} onClose={() => setPasswordTarget(null)} />}

      {deactivateTarget && (
        <ConfirmDialog
          title={deactivateTarget.active ? t('staff.deactivate') : t('staff.activate')}
          consequence={t('staff.deactivateConfirm')}
          danger={deactivateTarget.active}
          busy={busy}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setBusy(true);
            setError(null);
            try {
              const updated = target.active
                ? await staffApi.deactivate(target.id)
                : await staffApi.activate(target.id);
              setStaff((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
              setDeactivateTarget(null);
              flashStatus(t('common.saved'));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
              setDeactivateTarget(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
